/**
 * usePublishLayouts — PDF-6.8 PUBLISH via layout-tab multi-select.
 *
 * AutoCAD parity: the user shift/ctrl-clicks layout tabs to multi-select them,
 * then right-click → "Publish Selected Layouts" assembles ONE multi-page PDF
 * (one page per selected layout, in tab order). This hook owns the publish
 * action so `SpaceTabsBar` stays lean — it only holds the selection state.
 *
 * The hook reads the live document NON-reactively at click time
 * (`useEditorStore.getState().geometryDocument`), resolves each layout's
 * persisted `plotSettings` from the model (source of truth — see
 * `resolvePlotSettings`), builds the ordered `PublishSheet[]`, calls the real
 * `publishLayouts` backend, and downloads the produced bytes via the shared
 * `downloadPdfBytes`. Errors are surfaced (console.error) — never swallowed.
 */

import { useCallback } from "react";

import { useEditorStore } from "../state/editorStore";
import { publishLayouts } from "../print/pdf/publishLayouts";
import { resolvePlotSettings } from "../print/pdf/exportLayoutPdf";
import { downloadPdfBytes } from "../print/pdf/downloadPdfBytes";
import {
  defaultPdfExportConfig,
  dpiToToleranceMm,
  type PdfExportConfig,
} from "../print/pdf/pdfExportConfig";
import { buildPublishSheets } from "../components/spaceTabSelection";

export interface UsePublishLayoutsParams {
  /**
   * PDF-6.9b — non-reactive read of the committed PDF Options config.
   * Drives `toleranceMm` (from `vectorDpi`), `omitLayers`, and the
   * `includeBookmarks` gate on the `/Outlines` build. Optional — falls back
   * to {@link defaultPdfExportConfig} (1200 dpi, no omitted layers, bookmarks
   * ON) when absent, preserving the pre-6.9b behaviour.
   */
  readonly getPdfOptionsConfig?: () => PdfExportConfig;
}

export interface UsePublishLayoutsResult {
  /**
   * Publish the given LAYOUT space ids (already in VISIBLE tab order) to one
   * multi-page PDF and trigger a browser download. No-op for an empty list.
   * Async + guarded — resolves once the PDF is saved + download triggered.
   */
  publishSelected: (orderedLayoutSpaceIds: readonly string[]) => Promise<void>;
}

export function usePublishLayouts(
  params: UsePublishLayoutsParams = {},
): UsePublishLayoutsResult {
  const { getPdfOptionsConfig } = params;
  const publishSelected = useCallback(
    async (orderedLayoutSpaceIds: readonly string[]): Promise<void> => {
      if (orderedLayoutSpaceIds.length === 0) return;
      // Read the document at click time (non-reactive) so the publish reflects
      // the model right now — no stale closure.
      const doc = useEditorStore.getState().geometryDocument;
      const sheets = buildPublishSheets(
        doc,
        orderedLayoutSpaceIds,
        resolvePlotSettings,
      );
      // PDF-6.9b — non-reactive read of the committed PDF Options config.
      // Drives DPI → toleranceMm, the omit-layer list, and the bookmarks gate
      // for the whole multi-sheet PUBLISH.
      const pdfOptions = getPdfOptionsConfig?.() ?? defaultPdfExportConfig();
      try {
        const result = await publishLayouts(doc, sheets, {
          title: doc.title,
          toleranceMm: dpiToToleranceMm(pdfOptions.vectorDpi),
          omitLayers: pdfOptions.omitLayers,
          includeBookmarks: pdfOptions.includeBookmarks,
          // PDF-6.3b — same loaded plot-style table applied to every sheet
          // of the multi-sheet PUBLISH (one user choice → uniform overrides).
          ...(pdfOptions.plotStyleTable
            ? { plotStyleTable: pdfOptions.plotStyleTable.table }
            : {}),
          // PDF-6.4 — Lines merge / Overwrite. Same setting applied to every
          // sheet of the multi-page PUBLISH (one user choice → uniform blend).
          mergeControl: pdfOptions.mergeControl,
        });
        downloadPdfBytes(result.bytes, `${doc.title || "drawing"}-publish.pdf`);
      } catch (err: unknown) {
        // Never swallow silently — the command feed is not reachable from the
        // no-props SpaceTabsBar mount, so surface to the console (per task).
        const message = err instanceof Error ? err.message : String(err);
        console.error(`[PUBLISH] failed to publish layouts: ${message}`, err);
      }
    },
    [getPdfOptionsConfig],
  );

  return { publishSelected };
}
